import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, FileDown, Printer, Sheet, Archive, ArchiveRestore, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, openPdf, errMsg } from "@/lib/api";
import { fmtDate, fmtStamp } from "@/lib/format";
import { StatusBadge, LoadingBlock } from "@/components/Shared";
import ReportDocument from "@/components/ReportDocument";
import PhotoGallery from "@/components/PhotoGallery";

export default function ReportView({ admin }) {
  const { id } = useParams();
  const loc = useLocation();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const base = admin ? "/admin/reports" : "/reports";

  const load = async () => {
    try { setData((await api.get(`/reports/${id}`)).data); } catch (e) { toast.error(errMsg(e)); nav(admin ? "/admin/reports" : "/history"); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  if (!data) return <LoadingBlock />;
  const r = data.report;
  const ctx = `${data.client.name} · ${data.project.name} · ${fmtDate(r.report_date)}`;

  return (
    <div className="space-y-5 fade-up">
      <Link to={admin ? "/admin/reports" : "/history"} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-[#5C241E]" data-testid="report-back-link">
        <ArrowLeft className="h-4 w-4" />Back
      </Link>
      {loc.state?.justSubmitted && (
        <div className="flex items-center gap-3 rounded-lg border border-emerald-300 bg-emerald-50 p-4" data-testid="submit-success-banner">
          <CheckCircle2 className="h-8 w-8 shrink-0 text-emerald-600" />
          <div>
            <p className="font-semibold text-emerald-900">Report submitted successfully</p>
            <p className="text-sm text-emerald-800">Report #{r.report_number} with {data.photos.length} photo{data.photos.length === 1 ? "" : "s"} is now with the office.</p>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="eyebrow">Report #{r.report_number ?? "—"} · ID {r.id.slice(0, 8)}</p>
          <h1 className="mt-1 text-3xl font-bold sm:text-4xl" data-testid="report-title">{data.project.name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-600">
            <StatusBadge status={r.status} testId="report-status-badge" />
            <span>{fmtDate(r.report_date)}</span><span>·</span><span>{r.employee_name}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
          {data.can_edit && (
            <Link to={`${base}/${id}/edit`} className="btn-brand h-11" data-testid="report-edit-button"><Pencil className="h-4 w-4" />Edit</Link>
          )}
          <button className="btn-ghost h-11" disabled={!!busy} onClick={() => run("pdf", () => downloadFile(`/reports/${id}/export`, { format: "pdf" }))} data-testid="report-export-pdf-button"><FileDown className="h-4 w-4" />PDF</button>
          <button className="btn-ghost h-11" disabled={!!busy} onClick={() => run("xlsx", () => downloadFile(`/reports/${id}/export`, { format: "xlsx" }))} data-testid="report-export-xlsx-button"><Sheet className="h-4 w-4" />Excel</button>
          <button className="btn-ghost h-11" disabled={!!busy} onClick={() => run("print", () => openPdf(`/reports/${id}/export`, { format: "pdf", photos: false }))} data-testid="report-print-button"><Printer className="h-4 w-4" />Print</button>
          {admin && (r.status === "archived" ? (
            <button className="btn-ghost h-11" onClick={() => run("arch", async () => { await api.post(`/reports/${id}/unarchive`); toast.success("Report restored"); load(); })} data-testid="report-unarchive-button"><ArchiveRestore className="h-4 w-4" />Restore</button>
          ) : (
            <button className="btn-ghost h-11" onClick={() => run("arch", async () => { if (!window.confirm("Archive this report? It will be hidden from lists and exports.")) return; await api.post(`/reports/${id}/archive`); toast.success("Report archived"); load(); })} data-testid="report-archive-button"><Archive className="h-4 w-4" />Archive</button>
          ))}
        </div>
      </div>
      {busy && <p className="text-sm text-slate-500" data-testid="export-progress">Preparing file…</p>}

      <ReportDocument data={data} />

      <section className="panel p-4">
        <h2 className="mb-3 font-display text-xl font-semibold">Jobsite photos</h2>
        <PhotoGallery photos={data.photos} reportId={id} context={ctx} />
      </section>

      <section className="panel grid gap-3 p-4 text-sm sm:grid-cols-3" data-testid="report-meta">
        <div><p className="text-xs font-bold uppercase tracking-wider text-slate-500">Submitted</p><p className="font-semibold">{fmtStamp(r.submitted_at)}</p></div>
        <div><p className="text-xs font-bold uppercase tracking-wider text-slate-500">Last edited</p><p className="font-semibold">{fmtStamp(r.updated_at)}</p></div>
        <div><p className="text-xs font-bold uppercase tracking-wider text-slate-500">Edited by</p><p className="font-semibold">{r.updated_by_name || "—"}</p></div>
      </section>

      {admin && data.history?.length > 0 && (
        <section className="panel p-4" data-testid="report-history">
          <h2 className="mb-3 font-display text-xl font-semibold">Change history</h2>
          <ol className="space-y-3 border-l-2 border-[#5C241E]/20 pl-4">
            {data.history.map((h) => (
              <li key={h.id} className="text-sm">
                <p className="font-semibold">{h.user_name} · <span className="capitalize">{h.action.replace("_", " ")}</span> <span className="font-normal text-slate-500">— {fmtStamp(h.timestamp)}</span></p>
                {h.summary && <p className="text-slate-600">{h.summary}</p>}
                {Object.keys(h.changes || {}).length > 0 && (
                  <p className="text-xs text-slate-500">Changed: {Object.keys(h.changes).join(", ")}</p>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}
